import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, Zap } from 'lucide-react';

/**
 * Bridge page hit by /api/oauth/authorize when the user lacks an Authorization
 * header (the Zapier-opened tab does not auto-send our localStorage JWT).
 *
 * Flow:
 *   1. If not logged in → show a "Sign in" prompt.
 *   2. POST to /api/oauth/authorize with the JWT and the original query
 *      params; backend issues an authorization code and returns a
 *      `redirectUrl` pointing back to Zapier's return URL.
 *   3. window.location.replace that URL.
 */
const ZapOAuthConsent = () => {
  const location = useLocation();
  const [error, setError] = useState(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client_id');
    const redirectUri = params.get('redirect_uri');
    const responseType = params.get('response_type') || 'code';
    const state = params.get('state') || '';
    const scope = params.get('scope') || '';

    if (!clientId || !redirectUri) {
      setError('Missing client_id or redirect_uri.');
      return;
    }

    if (!token) {
      setNeedsLogin(true);
      return;
    }

    const authorize = async () => {
      setAuthorizing(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/oauth/authorize`,
          {
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: responseType,
            state,
            scope,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.success && res.data.redirectUrl) {
          window.location.replace(res.data.redirectUrl);
        } else {
          setError(res.data?.message || 'Authorization failed.');
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Authorization failed.';
        setError(msg);
      } finally {
        setAuthorizing(false);
      }
    };

    authorize();
  }, [location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#001233] p-6">
      <div className="w-full max-w-md bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853] mx-auto flex items-center justify-center mb-4">
          <Zap className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-white text-xl font-semibold mb-2">Connect to Zapier</h1>
        {needsLogin ? (
          <>
            <p className="text-sm text-gray-300 mb-6">
              Sign in to your R-Link Studio account, then return here to finish authorizing Zapier.
            </p>
            <a
              href="/login"
              className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 rounded-lg text-white text-sm font-medium transition-all"
            >
              Sign in
            </a>
            <p className="text-xs text-gray-500 mt-4">
              After signing in, click "Connect" again in Zapier.
            </p>
          </>
        ) : error ? (
          <>
            <div className="flex items-center justify-center gap-2 text-red-400 mb-4">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
            <p className="text-xs text-gray-400">
              Close this window and try connecting again from the Zapier app.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-300 mb-6">
              Authorizing your R-Link Studio account…
            </p>
            <Loader2 className="w-6 h-6 text-purple-400 animate-spin mx-auto" />
            {authorizing && (
              <p className="text-xs text-gray-500 mt-4">Redirecting back to Zapier…</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ZapOAuthConsent;
